<template>
    <b-modal
        :id="modalId"
        :title="isEdit ? '평가 그룹 수정' : '평가 그룹 추가'"
        centered
        no-close-on-backdrop
        :ok-title="isEdit ? '수정' : '생성'"
        cancel-title="취소"
        :ok-disabled="submitting"
        :cancel-disabled="submitting"
        @show="onShow"
        @ok="onOk"
    >
        <b-form @submit.stop.prevent="onOk">
            <!--
                group_code 는 프론트 빌드와 1:1 로 약속된 코드값이라 생성 시에만 입력받고
                수정 시에는 절대 바꾸지 못하도록 readonly 로 둔다 (바꾸면 연결 프로젝트가
                평가 신청 화면에서 통째로 사라진다).
            -->
            <b-form-group
                label="그룹 코드"
                label-for="pg-group-code"
                :description="isEdit
                    ? '그룹 코드는 프론트 빌드와 약속된 값이라 수정할 수 없습니다.'
                    : '프론트 빌드와 1:1로 약속된 코드값입니다. 생성 후에는 변경할 수 없습니다.'"
            >
                <b-form-input
                    id="pg-group-code"
                    v-model.trim="form.group_code"
                    :readonly="isEdit"
                    :state="isEdit ? null : (dirty.group_code ? !!form.group_code : null)"
                    placeholder="예: QESG_SUPPLY_2026"
                    autocomplete="off"
                    @input="dirty.group_code = true"
                />
            </b-form-group>

            <b-form-group label="그룹명 (표시명)" label-for="pg-group-name">
                <b-form-input
                    id="pg-group-name"
                    v-model.trim="form.group_name"
                    :state="dirty.group_name ? !!form.group_name : null"
                    placeholder="예: QESG 공급망 평가 2026"
                    autocomplete="off"
                    @input="dirty.group_name = true"
                />
            </b-form-group>

            <b-form-group
                label="보고서 엔진 (report_type)"
                label-for="pg-report-type"
                description="EV_COUNT 는 v3 신규 보고서 엔진, 미지정은 v2 기본 계산식으로 처리됩니다."
            >
                <b-form-select
                    id="pg-report-type"
                    v-model="form.report_type"
                    :options="reportTypeOptions"
                />
            </b-form-group>

            <b-form-group label="활성 여부">
                <b-form-checkbox v-model="form.is_active" switch>
                    {{ form.is_active ? '활성 (선택 드롭다운에 노출)' : '비활성 (드롭다운에서 숨김)' }}
                </b-form-checkbox>
            </b-form-group>

            <b-form-group label="운영 메모 (선택)" label-for="pg-memo">
                <b-form-textarea
                    id="pg-memo"
                    v-model="form.memo"
                    rows="2"
                    max-rows="5"
                    placeholder="운영자용 메모"
                />
            </b-form-group>
        </b-form>

        <template #modal-footer="{ cancel }">
            <b-button variant="secondary" :disabled="submitting" @click="cancel()">
                취소
            </b-button>
            <b-button variant="primary" :disabled="submitting" @click="onOk($event)">
                <b-spinner v-if="submitting" small class="mr-1" />
                {{ isEdit ? '수정' : '생성' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
export default {
    name: 'ProjectGroupFormModal',
    props: {
        modalId: {
            type: String,
            default: 'project-group-form-modal',
        },
        // 'create' | 'edit'
        mode: {
            type: String,
            default: 'create',
        },
        // 수정 대상 그룹 (create 모드에서는 무시)
        group: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            form: this.blankForm(),
            dirty: { group_code: false, group_name: false },
            submitting: false,
            reportTypeOptions: [
                { value: null, text: '기본 계산식 (v2)' },
                { value: 'EV_COUNT', text: 'EV_COUNT (v3 신규 보고서 엔진)' },
            ],
        };
    },
    computed: {
        isEdit() {
            return this.mode === 'edit';
        },
    },
    methods: {
        blankForm() {
            return {
                group_code: '',
                group_name: '',
                report_type: null,
                is_active: true,
                memo: '',
            };
        },
        // 모달이 열릴 때마다 props 기준으로 폼을 초기화한다.
        onShow() {
            this.dirty = { group_code: false, group_name: false };
            this.submitting = false;
            if (this.isEdit && this.group) {
                this.form = {
                    id: this.group.id,
                    group_code: this.group.group_code || '',
                    group_name: this.group.group_name || '',
                    // 빈문자열/undefined 는 기본 계산식(null)로 정규화
                    report_type: this.group.report_type || null,
                    is_active: this.group.is_active !== false,
                    memo: this.group.memo || '',
                };
            } else {
                this.form = this.blankForm();
            }
        },
        validate() {
            this.dirty.group_code = true;
            this.dirty.group_name = true;
            if (!this.isEdit && !this.form.group_code) {
                this.$bvToast.toast('그룹 코드를 입력해 주세요.', { title: '입력 확인', variant: 'warning' });
                return false;
            }
            if (!this.form.group_name) {
                this.$bvToast.toast('그룹명을 입력해 주세요.', { title: '입력 확인', variant: 'warning' });
                return false;
            }
            return true;
        },
        async onOk(evt) {
            // b-modal 기본 닫힘을 막고 직접 제어 (성공 시에만 닫는다)
            if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
            if (this.submitting) return;
            if (!this.validate()) return;

            this.submitting = true;
            try {
                const saved = this.isEdit ? await this.submitUpdate() : await this.submitCreate();
                this.$emit('saved', saved);
                this.$bvModal.hide(this.modalId);
            } catch (e) {
                this.handleError(e);
            } finally {
                this.submitting = false;
            }
        },
        async submitCreate() {
            const body = {
                group_code: this.form.group_code,
                group_name: this.form.group_name,
                report_type: this.form.report_type || null,
                is_active: this.form.is_active,
                memo: this.form.memo || '',
            };
            const { data } = await this.$axios.post('/project_code/groups', body);
            this.$bvToast.toast(`'${data.group_name}' 그룹을 생성했습니다.`, { title: '생성 완료', variant: 'success' });
            return data;
        },
        async submitUpdate() {
            // group_code 는 불변이라 전송하지 않는다 (서버도 무시하지만 명시적으로 제외).
            const body = {
                group_name: this.form.group_name,
                report_type: this.form.report_type || null,
                is_active: this.form.is_active,
                memo: this.form.memo || '',
            };
            const { data } = await this.$axios.put(`/project_code/groups/${this.form.id}`, body);
            this.$bvToast.toast(`'${data.group_name}' 그룹을 수정했습니다.`, { title: '수정 완료', variant: 'success' });
            return data;
        },
        handleError(e) {
            const status = e && e.response && e.response.status;
            const serverMsg = this.extractError(e);
            let msg;
            if (status === 409) {
                msg = serverMsg || '이미 존재하는 그룹 코드입니다. 다른 코드를 사용해 주세요.';
            } else if (status === 400) {
                msg = serverMsg || '입력값을 확인해 주세요.';
            } else if (status === 403) {
                msg = serverMsg || '권한이 없습니다. 관리자 계정으로 로그인했는지 확인해 주세요.';
            } else if (status === 404) {
                msg = serverMsg || '해당 그룹을 찾을 수 없습니다. 목록을 새로고침해 주세요.';
            } else {
                msg = serverMsg || '처리 중 오류가 발생했습니다.';
            }
            this.$bvToast.toast(msg, { title: '오류', variant: 'danger', noAutoHide: true });
        },
        // 백엔드 에러 응답 필드가 프로젝트마다 혼재(error/message/msg/detail)해 견고하게 추출한다.
        extractError(e) {
            const data = e && e.response && e.response.data;
            return getErrorMessage(e, '') || (data && (data.msg || data.detail)) || '';
        },
    },
};
</script>
